import { useEffect, useState } from 'react';
import { useStore } from 'src/contexts/StoreContext';
import {
  pollSession,
  getMediaItems,
  getImageFromMediaItem,
  startSession,
  deleteAccessToken,
  checkScopes,
  getAccountInfo,
  readAccessToken,
  isAccessTokenValid,
} from 'src/helpers/google.helper';
import { useSizeLimitContext } from 'src/contexts/SizeLimitContext';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { SourcesEventType } from '@photosi/rn-components/modals/dynamic-modals/modal-upload-multisource/ModalUploadMultisource.constants';
import { SCOPE_GOOGLE_PHOTO } from 'src/constants/google.constants';
import { filesToPickedImages } from 'src/_shared/utils/filesToPickedImages';
import { openWindow } from 'src/_shared/utils/open-window/openWindow';
import { EDITOR_BASE_URL } from 'src/app/settings';
import { ROUTE_NAMES, ROUTE_PATHS } from 'src/routes/_shared/routes.constants';
import { useRoute } from '@react-navigation/native';
import { convertObjectToQueryString } from 'src/_shared/utils/convertObjectToQueryString';
import { SELECTED_MENU_ITEMS } from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { usePolling } from 'src/hooks/usePolling';
import { logError, logWarning } from 'src/_shared/logger';
import { convertArrayBufferToFile } from 'src/helpers/image.helper';
import { SECTION_NAMES } from 'src/constants/sectionNames.contants';

export const usePickerGooglePhotoLogic = () => {
  const {
    i18n,
    toastStore,
    photoDrawer,
    googleAccount: {
      email,
      profilePic,
      isUserLogged,
      accessToken,
      setIsUserLogged,
      setEmail,
      setProfilePic,
      setAccessToken,
      setHasGivenAccessToPhoto,
      hasGivenAccessToPhoto,
    },
    project: { activeProject },
  } = useStore();

  const route = useRoute();
  const newRoute = [ROUTE_NAMES.EditorNew].includes(route?.name as ROUTE_NAMES);

  const screenSize = useSizeLimitContext();
  const { isDesktop } = useResponsiveScreen();

  const [sessionId, setSessionId] = useState<string>('');
  const [mediaItemsSet, setMediaItemsSet] = useState<boolean>(false);
  const { startPolling: startPollingCheckLogin, stopPolling: stopPollingCheckLogin } = usePolling();
  const { startPolling: startPollingSession, stopPolling: stopPollingSession } = usePolling();

  useEffect(() => {
    if (mediaItemsSet) {
      amplitude.trackEvent(AnalyticsEvents.dynamicPicker.choosePhotosClick, {
        source_type: SourcesEventType.GooglePhoto,
        section_name: SECTION_NAMES.DRAWER,
      });
      getMediaItems({ sessionId, accessToken }).then(async res => {
        // @ts-ignore
        const googlePhotos: GooglePhotoItem[] = res.filter(item => item.type === 'PHOTO');
        let pickedImages = [];
        if (newRoute) {
          pickedImages = await filesToPickedImages(
            googlePhotos,
            {
              i18n,
              errorCallback: ({ areAllPhotosInvalid, errorMessage }) => {
                if (areAllPhotosInvalid) {
                }

                toastStore.showErrorToast({
                  id: 'alert-uploading-photos-error-toast',
                  message: errorMessage,
                });
              },
            },
            newRoute,
          );
          const autoselect = activeProject!.thumbnailImages.length == 0;
          pickedImages.forEach(pickedImage => (pickedImage.source = SELECTED_MENU_ITEMS.GooglePhoto));
          await activeProject?.addImages(pickedImages, accessToken).then(() => {
            if (autoselect) {
              activeProject.thumbnailImages.forEach(image => {
                photoDrawer.addImageId(image.originalImageId);
              });
            }
            isDesktop && photoDrawer.setPicker(SELECTED_MENU_ITEMS.Computer);
          });
        } else {
          Promise.all(
            // @ts-ignore
            googlePhotos.map(item => {
              return getImageFromMediaItem({ item, accessToken }).then(async res => {
                return convertArrayBufferToFile({
                  arrayBuffer: res.data,
                  fileName: item.mediaFile.filename,
                  lastModified: new Date(item.createTime).getTime(),
                  mimeType: 'image/jpeg', // all google images fetched are in jpeg format
                });
              });
            }),
          ).then(async files => {
            pickedImages = await filesToPickedImages(
              files,
              {
                i18n,
                errorCallback: ({ areAllPhotosInvalid, errorMessage }) => {
                  if (areAllPhotosInvalid) {
                  }
                  toastStore.showErrorToast({
                    id: 'alert-uploading-photos-error-toast',
                    message: errorMessage,
                  });
                },
              },
              newRoute,
            );
            activeProject?.addImages(pickedImages, accessToken);
          });
        }

        amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourcePhotoUploaded, {
          n_photo: pickedImages.length,
          source_type: SourcesEventType.GooglePhoto,
          section_name: SECTION_NAMES.DRAWER,
        });
      });
    }
  }, [mediaItemsSet]);

  const handleLogin = async () => {
    amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourceAttachClick, {
      source_type: SourcesEventType.GooglePhoto,
    });

    await openWindow(`${EDITOR_BASE_URL}/${ROUTE_PATHS.GOOGLE_LOGIN}?${convertObjectToQueryString(route.params)}`, {
      target: 'null',
      dimensions: {
        width: (screenSize.width * 3) / 10,
        height: (screenSize.height * 6) / 10,
        left: (screenSize.width * 4) / 10,
        top: (screenSize.height * 2) / 10,
      },
    });

    startPollingCheckLogin(async () => {
      const accessToken = readAccessToken();
      if (accessToken) {
        try {
          await getAccountInfo({ accessToken: accessToken }).then(res => {
            setProfilePic(res.picture);
            setEmail(res.email);
          });
          await checkScopes(accessToken).then(res => {
            const userHasGivenAccessToPhoto = res.scope.includes(SCOPE_GOOGLE_PHOTO);
            if (userHasGivenAccessToPhoto) {
              setHasGivenAccessToPhoto(true);
              amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourceAttachSuccess, {
                source_type: SourcesEventType.GooglePhoto,
              });
            } else {
              setHasGivenAccessToPhoto(false);
              amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourceAttachError, {
                source_type: SourcesEventType.GooglePhoto,
                error: '',
              });
            }
          });
          setAccessToken(accessToken);
          setIsUserLogged(true);
          stopPollingCheckLogin();
        } catch (error) {
          logError(error);
        }
      }
      return void 0;
    });
  };

  const handleChoosePhoto = async () => {
    return startSession({ accessToken })
      .then(async res => {
        setSessionId(res.sessionId);
        startPollingSession(async () => {
          if (!mediaItemsSet) {
            try {
              if (!isAccessTokenValid()) {
                stopPollingSession();
                handleLogout();
                return;
              }
              const newMediaItemsSet = await pollSession({ sessionId: res.sessionId, accessToken });
              if (newMediaItemsSet) {
                setMediaItemsSet(newMediaItemsSet);
                stopPollingSession();
              }
            } catch (error) {
              logError(error);
            }
          }
        }, res.interval);
        await openWindow(
          res.pickerUri,
          isDesktop
            ? {
                target: 'null',
                dimensions: {
                  width: (screenSize.width * 6) / 10,
                  height: (screenSize.height * 6) / 10,
                  left: (screenSize.width * 2) / 10,
                  top: (screenSize.height * 2) / 10,
                },
              }
            : undefined,
        );
      })
      .catch(error => {
        logWarning('[Google Photo] - Choose Photo Error: ', error);
        setHasGivenAccessToPhoto(false);
      });
  };

  const handleLogout = () => {
    deleteAccessToken();
    setProfilePic('');
    setEmail('');
    setIsUserLogged(false);
    amplitude.trackEvent(AnalyticsEvents.photoSources.detachSourceClick, { source: SourcesEventType.GooglePhoto });
  };

  return {
    i18n,
    isDesktop,
    isUserLogged,
    hasGivenAccessToPhoto,
    handleChoosePhoto,
    handleLogin,
    email,
    profilePic,
    handleLogout,
  };
};
